import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCheckCircle,
  FaCompass,
  FaHiking,
  FaLandmark,
  FaLeaf,
  FaShoppingBag,
  FaSlidersH,
  FaUtensils,
} from "react-icons/fa";
import "../css/planYourJourney.css";
import NavBar from "./nav";
import Footer from "./footer";
import { findJourneyItem } from "./journeyData";

const interests = [
  { id: 1, name: "History", icon: FaLandmark, summary: "Museums, mining, and heritage stops" },
  { id: 2, name: "Nature", icon: FaLeaf, summary: "Dams, parks, wildlife, and open views" },
  { id: 3, name: "Adventure", icon: FaHiking, summary: "Trails, outdoor routes, and active days" },
  { id: 4, name: "Food", icon: FaUtensils, summary: "Restaurants, grills, cafes, and local taste" },
  { id: 5, name: "Shopping", icon: FaShoppingBag, summary: "Malls, markets, gifts, and essentials" },
];

const activities = {
  History: [
    {
      slug: "the-big-hole-walks",
      name: "Visit The Big Hole",
      category: "History",
      description:
        "Explore the story of diamond mining at Kimberley's most iconic historic landmark.",
      image: require("../assets/Fallback/Big Hole/bigHole04.jpg"),
    },
    {
      slug: "magersfontein-battlefield",
      name: "Magersfontein Battlefield",
      category: "History",
      description:
        "Walk through a battlefield landscape connected to the Anglo-Boer War.",
      image: require("../assets/Fallback/Magersfontein Battlefield/magersfonteinBattlefield05.jpg"),
    },
  ],
  Nature: [
    {
      slug: "kamfers-dam",
      name: "Kamfers Dam",
      category: "Nature",
      description:
        "A peaceful birding stop known for flamingos, open water, and quiet views.",
      image: require("../assets/KamferDam.jpg"),
    },
    {
      slug: "mokala-national-park",
      name: "Mokala National Park",
      category: "Nature",
      description:
        "Experience wildlife, rocky hills, savannah views, and national park scenery.",
      image: require("../assets/mokala-national-park-entrance-sign-01-500x300.jpg"),
    },
  ],
  Adventure: [
    {
      slug: "rooipoort-nature-reserve",
      name: "Rooipoort Nature Reserve",
      category: "Adventure",
      description:
        "Explore rugged trails and wilderness routes around the Kimberley area.",
      image: require("../assets/PL-RooiPoort.jpg"),
    },
    {
      slug: "skate-park",
      name: "Skate Park",
      category: "Adventure",
      description:
        "Add a more active city stop for younger travellers and casual outdoor fun.",
      image: require("../assets/Fallback/Skate Park/skatePark01.jpg"),
    },
  ],
  Food: [
    {
      slug: "kimberley-best-restaurants",
      name: "Kimberley's Best Restaurants",
      category: "Food",
      description:
        "Build a food stop into your route with restaurants, grills, cafes, and local favourites.",
      image: require("../assets/Fallback/The Hussar Grill/hussarGrill05.jpg"),
    },
    {
      slug: "local-cafe-stops",
      name: "Cafe & Casual Dining Stops",
      category: "Food",
      description:
        "Plan a relaxed break between attractions with easy dining and coffee stops.",
      image: require("../assets/Fallback/Occidental Bar & Restaurant/occidentalRestaurant01.jpg"),
    },
  ],
  Shopping: [
    {
      slug: "kimberley-diamond-mall",
      name: "Kimberley Mall Route",
      category: "Shopping",
      description:
        "Add a practical shopping stop for essentials, fashion, gifts, and dining.",
      image: require("../assets/Fallback/Northern Cape Mall/northern06.jpg"),
    },
    {
      slug: "local-markets",
      name: "Local Markets",
      category: "Shopping",
      description:
        "Look for gifts, handmade items, fresh produce, and small local finds.",
      image: require("../assets/Fallback/New Park Shopping Center/newPark01.jpg"),
    },
  ],
};

const PlanYourJourney = () => {
  const navigate = useNavigate();
  const [selectedInterests, setSelectedInterests] = useState(["History"]);

  const recommendedActivities = useMemo(
    () => selectedInterests.flatMap((interest) => activities[interest] || []),
    [selectedInterests]
  );

  const toggleInterest = (interest) => {
    setSelectedInterests((current) =>
      current.includes(interest)
        ? current.filter((item) => item !== interest)
        : [...current, interest]
    );
  };

  const openActivity = (activity) => {
    const existingItem = activity.slug ? findJourneyItem(activity.slug) : null;

    if (existingItem) {
      navigate(`/journey/${existingItem.slug}`, { state: { item: existingItem } });
      return;
    }

    navigate(`/journey/${activity.slug}`, {
      state: {
        item: {
          slug: activity.slug,
          name: activity.name,
          category: activity.category,
          location: "Kimberley",
          image: activity.image,
          description: activity.description,
          content: [
            activity.description,
            "This in-app guide gives visitors useful context when an external website is unavailable or unreliable.",
          ],
          highlights: ["Local recommendation", "Easy route planning", "Kimberley experience"],
          bestFor: [activity.category, "Visitors", "Trip planning"],
          visitorNotes:
            "Confirm operating times, access details, and availability before travelling.",
        },
      },
    });
  };

  return (
    <div className="plan-page">
      <NavBar />

      <section className="hero-section plan-hero">
        <div className="hero-content">
          <h1 className="hero-title">Plan Your Journey</h1>
          <p className="hero-subtitle">
            Choose what you love and build a Kimberley route around history,
            food, nature, shopping, and adventure.
          </p>
        </div>
      </section>

      <main className="plan-your-journey">
        <section className="plan-intro">
          <div>
            <p className="plan-eyebrow">
              <FaCompass /> Smart route builder
            </p>
            <h2>Choose Your Interests</h2>
            <p>
              Select one or more interests and we will shape a set of Kimberley
              recommendations you can explore inside the app.
            </p>
          </div>

          <div className="plan-summary-card">
            <FaSlidersH />
            <span>{selectedInterests.length}</span>
            <p>{selectedInterests.length === 1 ? "interest selected" : "interests selected"}</p>
          </div>
        </section>

        <section className="interest-selection" aria-label="Choose journey interests">
          {interests.map((interest) => {
            const Icon = interest.icon;
            const selected = selectedInterests.includes(interest.name);

            return (
              <button
                type="button"
                key={interest.id}
                className={`interest-card ${selected ? "selected" : ""}`}
                onClick={() => toggleInterest(interest.name)}
              >
                <span className="interest-icon-wrap">
                  <Icon className="interest-icon" />
                </span>
                <span className="interest-name">{interest.name}</span>
                <small>{interest.summary}</small>
                {selected && <FaCheckCircle className="interest-check" />}
              </button>
            );
          })}
        </section>

        <section className="recommended-activities">
          <div className="recommended-header">
            <div>
              <p className="plan-eyebrow">Personalised picks</p>
              <h2 className="recommended-title">Recommended Activities</h2>
            </div>
            <span>{recommendedActivities.length} suggestions</span>
          </div>

          {recommendedActivities.length > 0 ? (
            <div className="activity-grid">
              {recommendedActivities.map((activity) => (
                <article key={activity.slug} className="activity-card">
                  <div className="activity-media">
                    <img src={activity.image} alt={activity.name} className="activity-image" />
                    <span>{activity.category}</span>
                  </div>
                  <div className="activity-content">
                    <h3 className="activity-name">{activity.name}</h3>
                    <p className="activity-description">{activity.description}</p>
                    <button
                      type="button"
                      className="visit-button-2"
                      onClick={() => openActivity(activity)}
                    >
                      View Details <FaArrowRight />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="plan-empty-state">
              <FaCompass />
              <h3>Select an interest to begin</h3>
              <p>Your recommended Kimberley route ideas will appear here.</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PlanYourJourney;
